import { createFileRoute } from "@tanstack/react-router";
import * as React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  Plus, Trash2, Users, Calendar as Cal, Loader2, X, Pencil, Check, ChevronsUpDown, Download,
} from "lucide-react";
import { exportToCSV, todayStamp } from "@/lib/export-csv";
import { format } from "date-fns";
import { vi } from "date-fns/locale";
import { PortalLayout } from "@/components/portal-layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { UserAvatar } from "@/components/user-avatar";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useAuth, useCanMutate } from "@/lib/auth";
import { toast } from "sonner";
import { useServerFn } from "@tanstack/react-start";
import { createClassSessions, deleteClassSession, updateClassSession } from "@/lib/sessions.functions";
import { useEntitlements } from "@/hooks/use-entitlements";
import { UpgradeDialog } from "@/components/UpgradeDialog";

export const Route = createFileRoute("/admin/classes")({ component: ClassesPage });

type ClassStatus = "active" | "paused" | "completed";
type ClassRow = {
  id: string; name: string; subject: string | null;
  teacher_id: string | null; capacity: number; schedule_note: string | null;
  status: ClassStatus;
  class_code: string | null;
  enroll_count?: number;
};
type TeacherOpt = { id: string; display_name: string | null; email: string };
type StudentOpt = { id: string; display_name: string | null; email: string; avatar_url: string | null };

const STATUS_CONFIG: Record<ClassStatus, { label: string; className: string; dotColor: string }> = {
  active:    { label: "Đang học",    className: "bg-emerald-100 text-emerald-700 hover:bg-emerald-100", dotColor: "bg-emerald-500" },
  paused:    { label: "Tạm nghỉ",    className: "bg-amber-100 text-amber-700 hover:bg-amber-100",      dotColor: "bg-amber-500" },
  completed: { label: "Đã kết thúc", className: "bg-slate-100 text-slate-500 hover:bg-slate-100",      dotColor: "bg-slate-400" },
};

const StatusDot = ({ color }: { color: string }) => (
  <span className={`inline-block size-2 rounded-full ${color} mr-1.5 align-middle`} />
);

const DOW_LABEL = ["CN", "T2", "T3", "T4", "T5", "T6", "T7"];

let _myCenterIdCache: string | null | undefined;
let _myCenterIdUserId: string | undefined;
async function getMyCenterId(): Promise<string | null> {
  try {
    const { data: ures } = await supabase.auth.getUser();
    const uid = ures.user?.id;
    if (!uid) { _myCenterIdCache = null; _myCenterIdUserId = undefined; return null; }
    if (_myCenterIdUserId !== uid) {
      _myCenterIdCache = undefined;
      _myCenterIdUserId = uid;
    }
    if (_myCenterIdCache !== undefined) return _myCenterIdCache ?? null;
    const { data } = await supabase.from("profiles").select("center_id").eq("id", uid).maybeSingle();
    _myCenterIdCache = (data as { center_id?: string | null } | null)?.center_id ?? null;
    return _myCenterIdCache ?? null;
  } catch { _myCenterIdCache = null; return null; }
}

async function notifyClass(
  classId: string, title: string, body: string,
  audience: "students" | "teachers" | "all" = "all",
  createdBy?: string | null,
  centerId?: string | null,
) {
  try {
    const cid = centerId ?? await getMyCenterId();
    if (!cid) { console.warn("No center_id for notification"); return; }
    await supabase.from("announcements").insert({
      title, body, audience, class_id: classId, created_by: createdBy ?? null,
      center_id_owner: cid,
    });
  } catch (e) { console.error("notify error", e); }
}

async function notifyUser(userId: string, title: string, body: string, createdBy?: string | null, centerId?: string | null) {
  try {
    const cid = centerId ?? await getMyCenterId();
    if (!cid) { console.warn("No center_id for notification"); return; }
    await supabase.from("announcements").insert({
      title, body, audience: "all", class_id: null,
      target_user_id: userId,
      created_by: createdBy ?? userId,
      center_id_owner: cid,
    });
  } catch (e) { console.error("notify user error", e); }
}


function ClassesPage() {
  const qc = useQueryClient();
  const { isDemo, user } = useAuth();
  const canMutate = useCanMutate();
  const ent = useEntitlements();
  const [upgradeOpen, setUpgradeOpen] = React.useState(false);

  const refreshDashboardData = React.useCallback(() => {
    qc.invalidateQueries({ queryKey: ["entitlements"] });
    qc.invalidateQueries({ queryKey: ["admin-dashboard"] });
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("ezyclass:dashboard-refresh"));
    }
  }, [qc]);

  const classesQ = useQuery({
    queryKey: ["admin", "classes"],
    enabled: !isDemo,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("classes")
        .select("id, name, subject, teacher_id, capacity, schedule_note, status, class_code, created_at")
        .order("created_at", { ascending: false });
      if (error) throw error;
      const ids = (data ?? []).map((c) => c.id);
      const { data: counts } = ids.length
        ? await supabase.from("enrollments").select("class_id").in("class_id", ids).eq("is_active", true)
        : { data: [] as { class_id: string }[] };
      const countMap = new Map<string, number>();
      (counts ?? []).forEach((e) => countMap.set(e.class_id, (countMap.get(e.class_id) ?? 0) + 1));
      return (data ?? []).map((c: any) => ({
        ...c, enroll_count: countMap.get(c.id) ?? 0,
      })) as ClassRow[];
    },
  });

  const teachersQ = useQuery({
    queryKey: ["admin", "teachers"],
    enabled: !isDemo,
    queryFn: async () => {
      const { data: roles, error } = await supabase
        .from("user_roles").select("user_id").eq("role", "teacher");
      if (error) throw error;
      const ids = (roles ?? []).map((r) => r.user_id);
      if (!ids.length) return [] as TeacherOpt[];
      const { data: profs, error: e2 } = await supabase
        .from("profiles").select("id, display_name, email").in("id", ids);
      if (e2) throw e2;
      return (profs ?? []) as TeacherOpt[];
    },
  });

  const [open, setOpen] = React.useState(false);
  const [classStatusFilter, setClassStatusFilter] = React.useState<"all" | ClassStatus>("all");

  const createFn = useServerFn(createClassSessions);

  // Create form with schedule builder
  const tomorrow = React.useMemo(() => {
    const d = new Date(); d.setDate(d.getDate() + 1);
    return d.toISOString().split("T")[0];
  }, []);

  const [form, setForm] = React.useState({
    name: "", subject: "", teacher_id: "", capacity: 20,
    weekdays: [1, 4] as number[],
    startTime: "18:00",
    endTime: "19:30",
    room: "",
    startDate: tomorrow,
    weekCount: 12,
    hasSchedule: true,
  });

  const resetForm = () => setForm({
    name: "", subject: "", teacher_id: "", capacity: 20,
    weekdays: [1, 4], startTime: "18:00", endTime: "19:30",
    room: "", startDate: tomorrow, weekCount: 12, hasSchedule: true,
  });

  const scheduleNoteFromForm = (f: typeof form) => {
    if (!f.hasSchedule || f.weekdays.length === 0) return null;
    const sorted = [...f.weekdays].sort((a, b) => (a === 0 ? 7 : a) - (b === 0 ? 7 : b));
    const days = sorted.map((d) => DOW_LABEL[d]).join("-");
    return `${days} · ${f.startTime}–${f.endTime}`;
  };

  const createMut = useMutation({
    mutationFn: async () => {
      const centerId = user?.centerId;
      if (!centerId) {
        throw new Error("Vui lòng hoàn tất đăng ký trung tâm trước khi tạo lớp.");
      }
      if (form.hasSchedule && form.weekdays.length > 0 && form.startTime >= form.endTime) {
        throw new Error("Giờ kết thúc phải sau giờ bắt đầu.");
      }
      const note = scheduleNoteFromForm(form);
      // For free/tutor tiers (maxTeachers = 0), auto-assign admin as teacher.
      const effectiveTeacherId = ent.maxTeachers === 0 ? (user?.id ?? null) : (form.teacher_id || null);
      const { data: inserted, error } = await supabase.from("classes").insert({
        name: form.name.trim(),
        subject: form.subject.trim() || null,
        teacher_id: effectiveTeacherId,
        capacity: form.capacity,
        schedule_note: note,
        center_id: centerId,
      }).select("id").single();
      if (error) throw error;
      if (form.hasSchedule && form.weekdays.length > 0) {
        try {
          await createFn({
            data: {
              classId: inserted.id,
              startDate: form.startDate,
              startTime: form.startTime,
              endTime: form.endTime,
              room: form.room.trim() || undefined,
              recurring: true,
              weekdays: form.weekdays,
              weekCount: form.weekCount,
              tzOffsetMinutes: new Date().getTimezoneOffset(),
            },
          });
          notifyClass(inserted.id, "Lịch học mới", `Lịch học của lớp đã được tạo: ${note}`, "all", user?.id, user?.centerId);
        } catch (e: any) {
          toast.error("Tạo lớp xong nhưng không tạo được lịch: " + (e?.message ?? ""));
        }
      }
      return inserted;
    },
    onSuccess: () => {
      toast.success("Đã tạo lớp");
      qc.invalidateQueries({ queryKey: ["admin", "classes"] });
      refreshDashboardData();
      setOpen(false); resetForm();
    },
    onError: (e: any) => toast.error(e.message ?? "Không tạo được"),
  });

  const delMut = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("classes").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Đã xoá lớp");
      qc.invalidateQueries({ queryKey: ["admin", "classes"] });
      refreshDashboardData();
    },
    onError: (e: any) => toast.error(e.message ?? "Không xoá được"),
  });

  const teacherName = (id: string | null) => {
    if (!id) return "Chưa phân công";
    const t = teachersQ.data?.find((x) => x.id === id);
    return t?.display_name ?? t?.email ?? "—";
  };

  
  const [manageClassId, setManageClassId] = React.useState<string | null>(null);
  const manageClass = React.useMemo(
    () => (classesQ.data ?? []).find((c) => c.id === manageClassId) ?? null,
    [classesQ.data, manageClassId],
  );
  

  const filteredClasses = (classesQ.data ?? []).filter((c) =>
    classStatusFilter === "all" || (c.status ?? "active") === classStatusFilter,
  );

  const toggleFormDay = (v: number) => setForm((p) => ({
    ...p, weekdays: p.weekdays.includes(v) ? p.weekdays.filter((x) => x !== v) : [...p.weekdays, v],
  }));

  const previewSessions = form.hasSchedule && form.weekdays.length > 0
    ? form.weekdays.length * form.weekCount : 0;

  return (
    <PortalLayout
      portal="admin" title="Quản lý lớp học"
      breadcrumb={[{ label: "Admin", to: "/admin" }, { label: "Lớp học" }]}
    >
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <p className="text-sm text-muted-foreground">
            {filteredClasses.length} / {classesQ.data?.length ?? 0} lớp
          </p>
          <Select value={classStatusFilter} onValueChange={(v) => setClassStatusFilter(v as any)}>
            <SelectTrigger className="w-44 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tất cả lớp</SelectItem>
              <SelectItem value="active">Đang học</SelectItem>
              <SelectItem value="paused">Tạm nghỉ</SelectItem>
              <SelectItem value="completed">Đã kết thúc</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
          <Button
            disabled={!canMutate}
            className="gap-1.5"
            style={{ background: "var(--portal)", color: "white" }}
            onClick={() => {
              const current = classesQ.data?.length ?? 0;
              const maxClasses = ent.maxClasses === -1 ? Infinity : ent.maxClasses;
              if (current >= maxClasses) {
                setUpgradeOpen(true);
              } else {
                setOpen(true);
              }
            }}
          >
            <Plus className="size-4" /> Tạo lớp mới
          </Button>
          <DialogContent className="max-w-xl max-h-[88vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Tạo lớp mới</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Tên lớp</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="VD: Toán 9A" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Môn học</Label>
                  <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Toán" />
                </div>
                <div>
                  <Label>Sĩ số tối đa</Label>
                  <Input type="number" min={1} max={200} value={form.capacity}
                    onChange={(e) => setForm({ ...form, capacity: +e.target.value || 0 })} />
                </div>
              </div>
              {ent.maxTeachers === 0 ? (
                <div className="text-sm bg-muted/50 p-3 rounded-lg">
                  <span className="text-muted-foreground">Giáo viên: </span>
                  <strong>{user?.displayName ?? "Bạn"}</strong>
                  <span className="text-muted-foreground"> (admin)</span>
                </div>
              ) : (
                <div>
                  <Label>Giáo viên phụ trách</Label>
                  <Select value={form.teacher_id} onValueChange={(v) => setForm({ ...form, teacher_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Chọn giáo viên" /></SelectTrigger>
                    <SelectContent>
                      {(teachersQ.data ?? []).map((t) => (
                        <SelectItem key={t.id} value={t.id}>{t.display_name ?? t.email}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="rounded-lg border p-3 space-y-3 bg-muted/30">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-sm">Tạo lịch học ngay</div>
                    <div className="text-xs text-muted-foreground">Bỏ qua nếu muốn tạo sau</div>
                  </div>
                  <Switch checked={form.hasSchedule} onCheckedChange={(v) => setForm({ ...form, hasSchedule: v })} />
                </div>
                {form.hasSchedule && (
                  <>
                    <div>
                      <Label className="mb-1.5 block text-xs">Các ngày trong tuần</Label>
                      <div className="flex flex-wrap gap-1.5">
                        {[1,2,3,4,5,6,0].map((d) => (
                          <button type="button" key={d} onClick={() => toggleFormDay(d)}
                            className={`px-3 py-1.5 rounded-lg border text-sm transition ${
                              form.weekdays.includes(d)
                                ? "bg-[var(--portal)] text-white border-[var(--portal)]"
                                : "hover:bg-muted"
                            }`}>
                            {DOW_LABEL[d]}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Giờ bắt đầu</Label>
                        <Input type="time" value={form.startTime} onChange={(e) => setForm({ ...form, startTime: e.target.value })} />
                      </div>
                      <div>
                        <Label className="text-xs">Giờ kết thúc</Label>
                        <Input type="time" value={form.endTime} onChange={(e) => setForm({ ...form, endTime: e.target.value })} />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <Label className="text-xs">Phòng</Label>
                        <Input value={form.room} onChange={(e) => setForm({ ...form, room: e.target.value })} placeholder="P.101" />
                      </div>
                      <div>
                        <Label className="text-xs">Bắt đầu từ</Label>
                        <Input type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
                      </div>
                      <div>
                        <Label className="text-xs">Số tuần</Label>
                        <Input type="number" min={1} max={52} value={form.weekCount}
                          onChange={(e) => setForm({ ...form, weekCount: Math.max(1, Math.min(52, +e.target.value || 1)) })} />
                      </div>
                    </div>
                    {previewSessions > 0 && (
                      <div className="text-xs text-muted-foreground">
                        Sẽ tạo <strong className="text-foreground">{previewSessions}</strong> buổi học
                        {scheduleNoteFromForm(form) ? ` · ${scheduleNoteFromForm(form)}` : ""}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Huỷ</Button>
              <Button onClick={() => createMut.mutate()} disabled={!form.name.trim() || createMut.isPending}>
                {createMut.isPending && <Loader2 className="size-4 animate-spin mr-1" />}
                Lưu
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {isDemo && (
        <div className="mb-4 rounded-lg border bg-muted/40 p-3 text-sm text-muted-foreground">
          Bạn đang ở chế độ demo. Đăng nhập tài khoản admin thật để quản lý lớp.
        </div>
      )}

      {classesQ.isLoading ? (
        <div className="grid place-items-center py-20 text-muted-foreground">
          <Loader2 className="size-6 animate-spin" />
        </div>
      ) : classesQ.error ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          {(classesQ.error as Error).message}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredClasses.map((c, i) => {
            const st = c.status ?? "active";
            const cfg = STATUS_CONFIG[st];
            return (
              <motion.div key={c.id}
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                className={`rounded-2xl border bg-card p-4 hover:shadow-md transition-shadow cursor-pointer ${
                  st === "completed" ? "opacity-70" : ""
                }`}
                onClick={() => setManageClassId(c.id)}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-semibold leading-tight">{c.name}</div>
                      {c.class_code && (
                        <code className="text-[10px] px-1.5 py-0.5 bg-muted rounded font-mono text-muted-foreground">
                          {c.class_code}
                        </code>
                      )}
                      <Badge className={`text-[10px] ${cfg.className}`}><StatusDot color={cfg.dotColor} />{cfg.label}</Badge>
                    </div>
                    {c.subject && (
                      <Badge variant="secondary" className="mt-1 text-[10px]">{c.subject}</Badge>
                    )}
                  </div>
                  <Button size="icon" variant="ghost" disabled={!canMutate || delMut.isPending}
                    onClick={(e) => { e.stopPropagation(); if (confirm(`Xoá lớp "${c.name}"?`)) delMut.mutate(c.id); }}>
                    <Trash2 className="size-4 text-muted-foreground" />
                  </Button>
                </div>
                <div className="mt-3 space-y-1.5 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Users className="size-3.5" />
                    <span>{c.enroll_count ?? 0} / {c.capacity} học sinh</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Cal className="size-3.5" />
                    <span className="truncate">{c.schedule_note ?? "Chưa có lịch"}</span>
                  </div>
                  <div className="text-xs text-muted-foreground pt-1 border-t mt-2">
                    GV: <span className="text-foreground font-medium">{teacherName(c.teacher_id)}</span>
                  </div>
                  <div className="text-[11px] text-muted-foreground pt-1 italic">
                    Bấm để xem chi tiết & quản lý
                  </div>
                </div>
              </motion.div>
            );
          })}
          {filteredClasses.length === 0 && (
            <div className="col-span-full text-center py-12 text-muted-foreground border-2 border-dashed rounded-2xl">
              Không có lớp phù hợp bộ lọc.
            </div>
          )}
        </div>
      )}
      
      <ManageClassDialog
        cls={manageClass} onClose={() => setManageClassId(null)}
        canMutate={canMutate} teachers={teachersQ.data ?? []}
        currentUserId={user?.id ?? null}
      />
      <UpgradeDialog
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
        currentTier={ent.tier}
        resource="classes"
        currentCount={classesQ.data?.length ?? 0}
        limit={ent.maxClasses === -1 ? Infinity : ent.maxClasses}
      />
    </PortalLayout>
  );
}

/* ============================================================
   Helpers, sub-components
   ============================================================ */
const WEEKDAYS = [
  { v: 1, label: "T2" }, { v: 2, label: "T3" }, { v: 3, label: "T4" },
  { v: 4, label: "T5" }, { v: 5, label: "T6" }, { v: 6, label: "T7" },
  { v: 0, label: "CN" },
];

type SessionRow = { id: string; starts_at: string; ends_at: string; room: string | null };

/* ============================================================
   TEACHER COMBOBOX — searchable picker
   ============================================================ */
function TeacherCombobox({
  currentTeacherId, teachers, onSelect, disabled,
}: {
  currentTeacherId: string | null;
  teachers: TeacherOpt[];
  onSelect: (id: string) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const current = teachers.find((t) => t.id === currentTeacherId);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" disabled={disabled}
          className="w-full justify-between font-normal">
          <span className="truncate">
            {current ? (current.display_name ?? current.email.split("@")[0]) : "Chọn giáo viên..."}
          </span>
          <ChevronsUpDown className="size-4 opacity-50 ml-auto shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Tìm theo tên hoặc email..." />
          <CommandList>
            <CommandEmpty>Không tìm thấy</CommandEmpty>
            <CommandGroup>
              {currentTeacherId && (
                <CommandItem
                  value="bỏ phân công giáo viên"
                  onSelect={() => { onSelect(""); setOpen(false); }}
                >
                  <Check className="size-4 mr-2 opacity-0" />
                  <span className="text-sm text-muted-foreground">Bỏ phân công giáo viên</span>
                </CommandItem>
              )}
              {teachers.map((t) => (
                <CommandItem
                  key={t.id}
                  value={`${t.display_name ?? ""} ${t.email}`}
                  onSelect={() => { onSelect(t.id); setOpen(false); }}
                >
                  <Check className={cn("size-4 mr-2", currentTeacherId === t.id ? "opacity-100" : "opacity-0")} />
                  <div className="min-w-0">
                    <div className="text-sm truncate">{t.display_name ?? t.email.split("@")[0]}</div>
                    <div className="text-xs text-muted-foreground truncate">{t.email}</div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/* ============================================================
   STUDENTS TAB — search + checkbox multi-select
   ============================================================ */
function StudentsTab({
  classId, className, capacity, canMutate, currentUserId,
}: {
  classId: string;
  className: string;
  capacity: number;
  canMutate: boolean;
  currentUserId: string | null;
}) {
  const qc = useQueryClient();
  const [search, setSearch] = React.useState("");
  const [selected, setSelected] = React.useState<string[]>([]);
  const [adding, setAdding] = React.useState(false);

  const ent = useEntitlements();

  const enrolledQ = useQuery({
    queryKey: ["admin", "class-enrollments", classId],
    enabled: !!classId,
    queryFn: async () => {
      const { data: enrolls, error } = await supabase
        .from("enrollments").select("id, student_id, enrolled_at, is_active").eq("class_id", classId).eq("is_active", true);
      if (error) throw error;
      const ids = (enrolls ?? []).map((e) => e.student_id);
      if (!ids.length) return { enrolls: enrolls ?? [], students: [] as StudentOpt[] };
      const { data: profs } = await supabase
        .from("profiles").select("id, display_name, email, avatar_url, avatar_key").in("id", ids);
      const { data: privs } = await supabase.rpc("admin_list_profiles_private", { _ids: ids });
      const privMap = new Map(
        ((privs ?? []) as Array<{ id: string; phone: string | null }>).map((p) => [p.id, p.phone]),
      );
      const merged = ((profs ?? []) as Array<Record<string, unknown>>).map((s) => ({
        ...s,
        phone: privMap.get(s.id as string) ?? null,
      }));
      return { enrolls: enrolls ?? [], students: merged as any };
    },
  });

  const allStudentsQ = useQuery({
    queryKey: ["admin", "all-students"],
    enabled: !!classId,
    queryFn: async () => {
      const { data: roles, error } = await supabase
        .from("user_roles").select("user_id").eq("role", "student");
      if (error) throw error;
      const ids = (roles ?? []).map((r) => r.user_id);
      if (!ids.length) return [] as any[];
      const { data } = await supabase
        .from("profiles").select("id, display_name, email, avatar_url, avatar_key").in("id", ids);
      return (data ?? []) as any[];
    },
  });

  const enrolledIds = new Set((enrolledQ.data?.enrolls ?? []).map((e) => e.student_id));
  const enrollCount = enrolledQ.data?.students.length ?? 0;
  const available = (allStudentsQ.data ?? [])
    .filter((s) => !enrolledIds.has(s.id))
    .filter((s) => {
      if (!search.trim()) return true;
      const term = search.toLowerCase();
      return (s.display_name ?? "").toLowerCase().includes(term) || (s.email ?? "").toLowerCase().includes(term);
    });

  const removeMut = useMutation({
    mutationFn: async (vars: { enrollId: string; reason: string }) => {
      const { error } = await supabase.from("enrollments").update({
        is_active: false,
        left_at: new Date().toISOString(),
        reason: vars.reason,
      }).eq("id", vars.enrollId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Đã bỏ học sinh khỏi lớp");
      qc.invalidateQueries({ queryKey: ["admin", "class-enrollments", classId] });
      qc.invalidateQueries({ queryKey: ["admin", "classes"] });
      notifyClass(classId, "Học sinh rời lớp", `Một học sinh đã được bỏ khỏi lớp "${className}".`, "all", currentUserId);
    },
    onError: (e: any) => toast.error(e.message ?? "Không bỏ được"),
  });

  const [removeTarget, setRemoveTarget] = React.useState<{ id: string; name: string } | null>(null);
  const [removeReason, setRemoveReason] = React.useState<string>("removed");

  async function addSelected() {
    if (!selected.length) return;
    if (enrollCount + selected.length > capacity) {
      toast.error(`Vượt quá sĩ số (${capacity}). Chỉ còn ${capacity - enrollCount} chỗ trống.`);
      return;
    }
    setAdding(true);
    try {
      const { error } = await supabase.from("enrollments").insert(
        selected.map((sid) => ({ class_id: classId, student_id: sid })),
      );
      if (error) throw error;
      try {
        const cid = await getMyCenterId();
        await supabase.from("announcements").insert({
          title: "Bạn đã được ghi danh vào lớp mới",
          body: `Bạn đã được ghi danh vào lớp "${className}".`,
          audience: "students", class_id: classId, created_by: currentUserId,
          center_id_owner: cid,
        });
      } catch (e) { console.error("noti error", e); }
      toast.success(`Đã thêm ${selected.length} học sinh`);
      setSelected([]);
      qc.invalidateQueries({ queryKey: ["admin", "class-enrollments", classId] });
      qc.invalidateQueries({ queryKey: ["admin", "classes"] });
    } catch (e: any) {
      toast.error(e.message ?? "Lỗi");
    } finally {
      setAdding(false);
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-xs">Đang học ({enrollCount})</Label>
          <Button
            variant="outline"
            size="sm"
            className="h-7 gap-1.5 text-xs dark:border-slate-700"
            disabled={enrollCount === 0}
            title={ent.exportExcel ? "Xuất danh sách ra CSV" : "Nâng cấp gói Trung tâm để xuất dữ liệu"}
            onClick={() => {
              if (!ent.exportExcel) {
                toast.error("Nâng cấp gói Trung tâm để xuất dữ liệu");
                return;
              }
              const columns = [
                { key: "name", label: "Tên" },
                { key: "email", label: "Email" },
                { key: "phone", label: "SĐT" },
                { key: "joined", label: "Ngày vào lớp" },
                { key: "status", label: "Trạng thái" },
              ];
              const enrollMap = new Map(
                (enrolledQ.data?.enrolls ?? []).map((e: any) => [e.student_id, e]),
              );
              const rows = (enrolledQ.data?.students ?? []).map((s: any) => {
                const e: any = enrollMap.get(s.id);
                return {
                  name: s.display_name ?? "",
                  email: s.email ?? "",
                  phone: s.phone ?? "",
                  joined: e?.enrolled_at ? new Date(e.enrolled_at).toLocaleDateString("vi-VN") : "",
                  status: "Đang học",
                };
              });
              exportToCSV(`HS_${className}_${todayStamp()}`, columns, rows);
              toast.success(`Đã xuất ${rows.length} dòng`);
            }}
          >
            <Download className="size-3.5" /> Xuất CSV
          </Button>
        </div>
        {enrollCount === 0 ? (
          <p className="text-sm text-muted-foreground">Chưa có học sinh nào.</p>
        ) : (
          <div className="space-y-1.5 max-h-52 overflow-y-auto pr-1">
            {(enrolledQ.data?.students ?? []).map((s: any) => {
              const enroll = (enrolledQ.data?.enrolls ?? []).find((e) => e.student_id === s.id);
              return (
                <div key={s.id} className="flex items-center justify-between p-2 rounded-lg border hover:bg-muted/30 transition-colors">
                  <div className="flex items-center gap-2 min-w-0">
                    <UserAvatar avatarKey={s.avatar_key} fallback={s.display_name ?? s.email} size="sm" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{s.display_name ?? s.email.split("@")[0]}</div>
                      <div className="text-xs text-muted-foreground truncate">{s.email}</div>
                    </div>
                  </div>
                  <Button size="icon" variant="ghost" className="shrink-0"
                    disabled={!canMutate || removeMut.isPending}
                    onClick={() => {
                      if (enroll) {
                        setRemoveReason("removed");
                        setRemoveTarget({ id: enroll.id, name: s.display_name ?? s.email });
                      }
                    }}>
                    <X className="size-4" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="pt-3 border-t">
        <Label className="text-xs mb-2 block">Thêm học sinh vào lớp</Label>
        <Input
          placeholder="Tìm theo tên hoặc email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="mb-2"
        />
        <div className="max-h-52 overflow-y-auto border rounded-lg">
          {available.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground text-center">
              {search ? "Không tìm thấy" : "Tất cả học sinh đã trong lớp"}
            </div>
          ) : (
            available.map((s: any) => (
              <label key={s.id} className="flex items-center gap-3 p-2.5 hover:bg-muted/40 cursor-pointer border-b last:border-0 transition-colors">
                <input
                  type="checkbox"
                  checked={selected.includes(s.id)}
                  onChange={(e) => {
                    setSelected((prev) => e.target.checked
                      ? [...prev, s.id]
                      : prev.filter((id) => id !== s.id));
                  }}
                  className="size-4 rounded accent-blue-600"
                />
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <UserAvatar avatarKey={s.avatar_key} fallback={s.display_name ?? s.email} size="sm" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{s.display_name ?? s.email.split("@")[0]}</div>
                    <div className="text-xs text-muted-foreground truncate">{s.email}</div>
                  </div>
                </div>
              </label>
            ))
          )}
        </div>
        {selected.length > 0 && (
          <Button className="mt-2 w-full" onClick={addSelected} disabled={adding || !canMutate}>
            {adding ? <Loader2 className="size-4 animate-spin mr-1" /> : null}
            Thêm {selected.length} học sinh vào lớp
          </Button>
        )}
      </div>

      <Dialog open={!!removeTarget} onOpenChange={(o) => !o && setRemoveTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Bỏ học sinh khỏi lớp</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm">
              Bỏ <span className="font-medium">{removeTarget?.name}</span> khỏi lớp{" "}
              <span className="font-medium">{className}</span>?
            </p>
            <div>
              <Label className="text-xs mb-1.5 block">Lý do</Label>
              <Select value={removeReason} onValueChange={setRemoveReason}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="dropped">✕ Rời lớp</SelectItem>
                  <SelectItem value="transferred">↔ Chuyển lớp</SelectItem>
                  <SelectItem value="completed">Hoàn thành</SelectItem>
                  <SelectItem value="removed">Khác</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoveTarget(null)}>Huỷ</Button>
            <Button
              disabled={removeMut.isPending}
              onClick={() => {
                if (!removeTarget) return;
                removeMut.mutate(
                  { enrollId: removeTarget.id, reason: removeReason },
                  { onSettled: () => setRemoveTarget(null) },
                );
              }}
            >
              {removeMut.isPending && <Loader2 className="size-4 animate-spin mr-1" />}
              Xác nhận
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ============================================================
   SCHEDULE CONTENT — create/list sessions with inline edit
   ============================================================ */
function ScheduleContent({
  classId, className, canMutate, currentUserId,
}: {
  classId: string;
  className: string;
  canMutate: boolean;
  currentUserId: string | null;
}) {
  const qc = useQueryClient();
  const createFn = useServerFn(createClassSessions);
  const deleteFn = useServerFn(deleteClassSession);
  const updateFn = useServerFn(updateClassSession);

  const tomorrow = React.useMemo(() => {
    const d = new Date(); d.setDate(d.getDate() + 1);
    return d.toISOString().split("T")[0];
  }, []);

  const [startDate, setStartDate] = React.useState(tomorrow);
  const [startTime, setStartTime] = React.useState("18:00");
  const [endTime, setEndTime] = React.useState("19:30");
  const [room, setRoom] = React.useState("");
  const [recurring, setRecurring] = React.useState(true);
  const [weekdays, setWeekdays] = React.useState<number[]>([1, 4]);
  const [weekCount, setWeekCount] = React.useState(12);
  const hasUserEditedRef = React.useRef(false);
  const markDirty = () => { hasUserEditedRef.current = true; };

  const sessionsQ = useQuery({
    queryKey: ["admin", "class-sessions", classId],
    enabled: !!classId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("class_sessions").select("id, starts_at, ends_at, room")
        .eq("class_id", classId).order("starts_at");
      if (error) throw error;
      return (data ?? []) as SessionRow[];
    },
  });

  // Suy ra pattern lịch định kỳ từ các buổi tương lai
  const derivedPattern = React.useMemo(() => {
    const all = sessionsQ.data ?? [];
    const now = Date.now();
    const future = all.filter((s) => new Date(s.starts_at).getTime() >= now);
    if (future.length === 0) return null;
    const pad = (n: number) => String(n).padStart(2, "0");
    const first = future[0];
    const firstStart = new Date(first.starts_at);
    const firstEnd = new Date(first.ends_at);
    const dateStr = `${firstStart.getFullYear()}-${pad(firstStart.getMonth() + 1)}-${pad(firstStart.getDate())}`;
    const startT = `${pad(firstStart.getHours())}:${pad(firstStart.getMinutes())}`;
    const endT = `${pad(firstEnd.getHours())}:${pad(firstEnd.getMinutes())}`;
    const horizon = firstStart.getTime() + 28 * 24 * 3600 * 1000;
    const dows = new Set<number>();
    const weekKeys = new Set<string>();
    future.forEach((s) => {
      const d = new Date(s.starts_at);
      if (d.getTime() <= horizon) dows.add(d.getDay());
      const tmp = new Date(d); tmp.setHours(0, 0, 0, 0);
      tmp.setDate(tmp.getDate() - ((tmp.getDay() + 6) % 7));
      weekKeys.add(`${tmp.getFullYear()}-${tmp.getMonth()}-${tmp.getDate()}`);
    });
    const wd = Array.from(dows);
    const isRecurring = future.length >= 2 && wd.length >= 1;
    return {
      startDate: dateStr,
      startTime: startT,
      endTime: endT,
      room: first.room ?? "",
      weekdays: wd.length > 0 ? wd : [firstStart.getDay()],
      weekCount: Math.max(1, Math.min(52, weekKeys.size || 1)),
      recurring: isRecurring,
      futureCount: future.length,
    };
  }, [sessionsQ.data]);

  // Prefill state khi pattern đổi và user chưa chạm form
  React.useEffect(() => {
    if (!derivedPattern || hasUserEditedRef.current) return;
    setStartDate(derivedPattern.startDate);
    setStartTime(derivedPattern.startTime);
    setEndTime(derivedPattern.endTime);
    setRoom(derivedPattern.room);
    setWeekdays(derivedPattern.weekdays);
    setWeekCount(derivedPattern.weekCount);
    setRecurring(derivedPattern.recurring);
  }, [derivedPattern]);

  const futureCount = derivedPattern?.futureCount ?? 0;
  const previewCount = recurring && weekdays.length > 0 ? weekdays.length * weekCount : 1;

  const saveMut = useMutation({
    mutationFn: async () => {
      if (startTime >= endTime) {
        throw new Error("Giờ kết thúc phải sau giờ bắt đầu.");
      }
      const nowIso = new Date().toISOString();
      const { error: delErr } = await supabase
        .from("class_sessions").delete()
        .eq("class_id", classId).gte("starts_at", nowIso);
      if (delErr) throw new Error(delErr.message);
      return createFn({
        data: {
          classId, startDate, startTime, endTime,
          room: room.trim() || undefined,
          recurring, weekdays, weekCount,
          tzOffsetMinutes: new Date().getTimezoneOffset(),
        },
      });
    },
    onSuccess: (r) => {
      toast.success(`Đã cập nhật lịch (${r.created} buổi)`);
      hasUserEditedRef.current = false;
      qc.invalidateQueries({ queryKey: ["admin", "class-sessions", classId] });
      const sortedDays = [...weekdays].sort((a, b) => (a === 0 ? 7 : a) - (b === 0 ? 7 : b));
      const dayLabels = sortedDays.map((d) => WEEKDAYS.find((w) => w.v === d)?.label).join(", ");
      const summary = recurring
        ? `${dayLabels} · ${startTime}–${endTime}${room.trim() ? ` · ${room.trim()}` : ""} · ${weekCount} tuần`
        : `${startDate} · ${startTime}–${endTime}${room.trim() ? ` · ${room.trim()}` : ""}`;
      notifyClass(classId, "Lịch học đã thay đổi",
        `Lịch lớp "${className}" đã được cập nhật: ${summary}.`, "all", currentUserId);
    },
    onError: (e: any) => toast.error(e?.message ?? "Không lưu được"),
  });

  const delMut = useMutation({
    mutationFn: async (s: SessionRow) => {
      await deleteFn({ data: { sessionId: s.id } });
      return s;
    },
    onSuccess: (s) => {
      toast.success("Đã xoá buổi học");
      qc.invalidateQueries({ queryKey: ["admin", "class-sessions", classId] });
      notifyClass(classId, "Buổi học đã bị hủy",
        `Buổi học ngày ${format(new Date(s.starts_at), "dd/MM/yyyy HH:mm")} của lớp "${className}" đã bị hủy.`,
        "all", currentUserId);
    },
    onError: (e: any) => toast.error(e?.message ?? "Không xoá được"),
  });

  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [editDate, setEditDate] = React.useState("");
  const [editStart, setEditStart] = React.useState("");
  const [editEnd, setEditEnd] = React.useState("");
  const [editRoom, setEditRoom] = React.useState("");

  const beginEdit = (s: SessionRow) => {
    const sd = new Date(s.starts_at);
    const ed = new Date(s.ends_at);
    const pad = (n: number) => String(n).padStart(2, "0");
    setEditDate(`${sd.getFullYear()}-${pad(sd.getMonth() + 1)}-${pad(sd.getDate())}`);
    setEditStart(`${pad(sd.getHours())}:${pad(sd.getMinutes())}`);
    setEditEnd(`${pad(ed.getHours())}:${pad(ed.getMinutes())}`);
    setEditRoom(s.room ?? "");
    setEditingId(s.id);
  };

  const updateMut = useMutation({
    mutationFn: async () => {
      if (editStart >= editEnd) {
        throw new Error("Giờ kết thúc phải sau giờ bắt đầu.");
      }
      const startsAt = new Date(`${editDate}T${editStart}:00`).toISOString();
      const endsAt = new Date(`${editDate}T${editEnd}:00`).toISOString();
      await updateFn({ data: { sessionId: editingId!, startsAt, endsAt, room: editRoom.trim() || null } });
      return { startsAt, endsAt };
    },
    onSuccess: (r) => {
      toast.success("Đã lưu");
      setEditingId(null);
      qc.invalidateQueries({ queryKey: ["admin", "class-sessions", classId] });
      notifyClass(classId, "Lịch học đã thay đổi",
        `Một buổi học của lớp "${className}" đã được cập nhật sang ${format(new Date(r.startsAt), "HH:mm dd/MM/yyyy")}.`,
        "all", currentUserId);
    },
    onError: (e: any) => toast.error(e?.message ?? "Không lưu được"),
  });

  const grouped = React.useMemo(() => {
    const m = new Map<string, SessionRow[]>();
    (sessionsQ.data ?? []).forEach((s) => {
      const k = format(new Date(s.starts_at), "yyyy-MM");
      const arr = m.get(k) ?? [];
      arr.push(s); m.set(k, arr);
    });
    return Array.from(m.entries());
  }, [sessionsQ.data]);

  const toggleDay = (v: number) => {
    markDirty();
    setWeekdays((prev) => prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]);
  };

  return (
    <Tabs defaultValue="create">
      <TabsList className="grid w-full grid-cols-2">
        <TabsTrigger value="create">Lịch định kỳ</TabsTrigger>
        <TabsTrigger value="list">Danh sách ({sessionsQ.data?.length ?? 0})</TabsTrigger>
      </TabsList>

      <TabsContent value="create" className="space-y-4 pt-4">
        <div className="rounded-lg border bg-muted/30 p-3 text-xs text-muted-foreground">
          Form này phản ánh lịch định kỳ hiện tại của lớp. Khi bấm <strong>Lưu lịch</strong>, các buổi tương lai chưa diễn ra sẽ bị thay thế theo pattern mới — các buổi đã qua được giữ nguyên.
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Giờ bắt đầu</Label>
            <Input type="time" value={startTime} onChange={(e) => { markDirty(); setStartTime(e.target.value); }} />
          </div>
          <div>
            <Label>Giờ kết thúc</Label>
            <Input type="time" value={endTime} onChange={(e) => { markDirty(); setEndTime(e.target.value); }} />
          </div>
        </div>
        <div>
          <Label>Phòng học</Label>
          <Input value={room} onChange={(e) => { markDirty(); setRoom(e.target.value); }} placeholder="P.301" />
        </div>
        <div className="flex items-center justify-between p-3 rounded-lg border">
          <div>
            <div className="font-medium text-sm">Lặp lại theo tuần</div>
            <div className="text-xs text-muted-foreground">Tạo nhiều buổi học cùng lúc</div>
          </div>
          <Switch checked={recurring} onCheckedChange={(v) => { markDirty(); setRecurring(v); }} />
        </div>
        {recurring ? (
          <div className="space-y-3">
            <div>
              <Label className="mb-2 block">Các ngày trong tuần</Label>
              <div className="flex flex-wrap gap-2">
                {WEEKDAYS.map((d) => (
                  <label key={d.v}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border cursor-pointer text-sm ${
                      weekdays.includes(d.v) ? "bg-primary text-primary-foreground border-primary" : "hover:bg-muted"
                    }`}>
                    <Checkbox checked={weekdays.includes(d.v)}
                      onCheckedChange={() => toggleDay(d.v)} className="h-3.5 w-3.5" />
                    {d.label}
                  </label>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Bắt đầu từ ngày</Label>
                <Input type="date" value={startDate} onChange={(e) => { markDirty(); setStartDate(e.target.value); }} />
              </div>
              <div>
                <Label>Số tuần</Label>
                <Input type="number" min={1} max={52} value={weekCount}
                  onChange={(e) => { markDirty(); setWeekCount(Math.max(1, Math.min(52, +e.target.value || 1))); }} />
              </div>
            </div>
          </div>
        ) : (
          <div>
            <Label>Ngày học</Label>
            <Input type="date" value={startDate} onChange={(e) => { markDirty(); setStartDate(e.target.value); }} />
          </div>
        )}
        <div className="rounded-lg bg-muted/50 p-3 text-sm flex items-center justify-between gap-3">
          <span>
            Lịch mới: <strong>{previewCount}</strong> buổi
            {futureCount > 0 && (
              <span className="text-muted-foreground"> · sẽ thay thế {futureCount} buổi tương lai</span>
            )}
          </span>
          <Button disabled={!canMutate || saveMut.isPending || (recurring && weekdays.length === 0)}
            onClick={() => {
              if (futureCount > 0 && !confirm(`Sẽ xoá ${futureCount} buổi tương lai và tạo lại ${previewCount} buổi theo lịch mới. Tiếp tục?`)) return;
              saveMut.mutate();
            }}>
            {saveMut.isPending && <Loader2 className="size-4 animate-spin mr-1" />}
            Lưu lịch
          </Button>
        </div>

      </TabsContent>

      <TabsContent value="list" className="pt-4">
        {sessionsQ.isLoading ? (
          <div className="grid place-items-center py-12"><Loader2 className="size-5 animate-spin" /></div>
        ) : (sessionsQ.data?.length ?? 0) === 0 ? (
          <div className="text-center py-12 text-sm text-muted-foreground border-2 border-dashed rounded-xl">
            Chưa có buổi học nào.
          </div>
        ) : (
          <div className="space-y-4">
            {grouped.map(([month, items]) => (
              <div key={month}>
                <div className="text-xs uppercase font-medium text-muted-foreground mb-2">
                  {format(new Date(month + "-01"), "MMMM yyyy", { locale: vi })}
                </div>
                <div className="space-y-1.5">
                  {items.map((s) => {
                    const isEditing = editingId === s.id;
                    return (
                      <div key={s.id}
                        className={`rounded-lg border text-sm transition ${
                          isEditing ? "border-[var(--portal)] bg-[var(--portal-soft)]" : ""
                        }`}>
                        {isEditing ? (
                          <div className="p-3 space-y-2">
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <Label className="text-xs">Ngày</Label>
                                <Input type="date" value={editDate} onChange={(e) => setEditDate(e.target.value)} />
                              </div>
                              <div>
                                <Label className="text-xs">Phòng</Label>
                                <Input value={editRoom} onChange={(e) => setEditRoom(e.target.value)} placeholder="P.101" />
                              </div>
                              <div>
                                <Label className="text-xs">Bắt đầu</Label>
                                <Input type="time" value={editStart} onChange={(e) => setEditStart(e.target.value)} />
                              </div>
                              <div>
                                <Label className="text-xs">Kết thúc</Label>
                                <Input type="time" value={editEnd} onChange={(e) => setEditEnd(e.target.value)} />
                              </div>
                            </div>
                            <div className="flex justify-end gap-2">
                              <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>Hủy</Button>
                              <Button size="sm" onClick={() => updateMut.mutate()} disabled={updateMut.isPending}>
                                {updateMut.isPending && <Loader2 className="size-3 animate-spin mr-1" />}
                                Lưu
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between p-2.5">
                            <div>
                              <div className="font-medium">
                                {format(new Date(s.starts_at), "EEEE, dd/MM", { locale: vi })}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {format(new Date(s.starts_at), "HH:mm")}–{format(new Date(s.ends_at), "HH:mm")}
                                {s.room ? ` · ${s.room}` : ""}
                              </div>
                            </div>
                            <div className="flex gap-0.5">
                              <Button size="icon" variant="ghost" disabled={!canMutate}
                                onClick={() => beginEdit(s)}>
                                <Pencil className="size-4 text-muted-foreground" />
                              </Button>
                              <Button size="icon" variant="ghost"
                                disabled={!canMutate || delMut.isPending}
                                onClick={() => { if (confirm("Xoá buổi học này?")) delMut.mutate(s); }}>
                                <X className="size-4" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </TabsContent>
    </Tabs>
  );
}

/* ============================================================
   MANAGE CLASS DIALOG — 3 tabs: info / schedule / students
   ============================================================ */
function ManageClassDialog({
  cls, onClose, canMutate, teachers, currentUserId,
}: {
  cls: ClassRow | null;
  onClose: () => void;
  canMutate: boolean;
  teachers: TeacherOpt[];
  currentUserId: string | null;
}) {
  const qc = useQueryClient();

  const enrollCountQ = useQuery({
    queryKey: ["admin", "class-enroll-count", cls?.id],
    enabled: !!cls,
    queryFn: async () => {
      const { count } = await supabase
        .from("enrollments").select("id", { count: "exact", head: true })
        .eq("class_id", cls!.id).eq("is_active", true);
      return count ?? 0;
    },
  });

  const statusMut = useMutation({
    mutationFn: async (s: ClassStatus) => {
      const { error } = await supabase.from("classes").update({ status: s }).eq("id", cls!.id);
      if (error) throw error;
      return s;
    },
    onSuccess: (s) => {
      toast.success("Đã đổi trạng thái lớp");
      qc.invalidateQueries({ queryKey: ["admin", "classes"] });
      notifyClass(cls!.id, "Lớp học thay đổi trạng thái",
        `Lớp "${cls!.name}" chuyển sang trạng thái: ${STATUS_CONFIG[s].label}`,
        "all", currentUserId);
    },
    onError: (e: any) => toast.error(e.message ?? "Không đổi được"),
  });

  const teacherMut = useMutation({
    mutationFn: async (teacherId: string) => {
      const oldId = cls!.teacher_id;
      const { error } = await supabase.from("classes").update({
        teacher_id: teacherId || null,
      }).eq("id", cls!.id);
      if (error) throw error;
      // Chờ danh sách lớp tải lại xong để dialog hiển thị ngay giá trị mới
      await qc.invalidateQueries({ queryKey: ["admin", "classes"] });
      return { newId: teacherId, oldId };
    },
    onSuccess: ({ newId, oldId }) => {
      toast.success(newId ? "Đã đổi giáo viên phụ trách" : "Đã bỏ phân công giáo viên");
      if (newId) notifyUser(newId, "Bạn được phân công lớp mới",
        `Bạn được phân công phụ trách lớp "${cls!.name}".`, currentUserId);
      if (oldId && oldId !== newId) notifyUser(oldId, "Bạn được tháo khỏi lớp",
        `Bạn không còn phụ trách lớp "${cls!.name}".`, currentUserId);
      notifyClass(cls!.id, "Đổi giáo viên phụ trách",
        newId
          ? `Lớp "${cls!.name}" có giáo viên phụ trách mới.`
          : `Lớp "${cls!.name}" hiện chưa có giáo viên phụ trách.`,
        "students", currentUserId);
    },
    onError: (e: any) => toast.error(e.message ?? "Không đổi được"),
  });

  if (!cls) return null;
  const enrollCount = enrollCountQ.data ?? 0;
  const pct = Math.min(100, Math.round((enrollCount / Math.max(1, cls.capacity)) * 100));
  const st = cls.status ?? "active";

  return (
    <Dialog open={!!cls} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            {cls.name}
            {cls.class_code && (
              <code className="text-[11px] px-1.5 py-0.5 bg-muted rounded font-mono text-muted-foreground font-normal">
                {cls.class_code}
              </code>
            )}
            <Badge className={STATUS_CONFIG[st].className}>
              <StatusDot color={STATUS_CONFIG[st].dotColor} />{STATUS_CONFIG[st].label}
            </Badge>
            {cls.subject && <Badge variant="secondary" className="text-[10px]">{cls.subject}</Badge>}
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="info" className="mt-2">
          <TabsList className="w-full grid grid-cols-3">
            <TabsTrigger value="info">Thông tin</TabsTrigger>
            <TabsTrigger value="schedule">Lịch học</TabsTrigger>
            <TabsTrigger value="students">Học sinh</TabsTrigger>
          </TabsList>

          {/* TAB 1: THÔNG TIN — view + edit */}
          <TabsContent value="info" className="mt-4">
            <EditClassInfo
              cls={cls}
              canMutate={canMutate}
              teachers={teachers}
              currentUserId={currentUserId}
              enrollCount={enrollCount}
              pct={pct}
              onStatusChange={(s) => {
                if (s === "completed" && !confirm(`Đánh dấu lớp "${cls.name}" đã kết thúc? GV và HS sẽ được thông báo.`)) return;
                statusMut.mutate(s);
              }}
              onTeacherChange={(teacherId) => {
                if ((teacherId || null) === (cls.teacher_id ?? null)) return;
                if (!confirm(teacherId ? "Đổi giáo viên phụ trách lớp này?" : "Bỏ phân công giáo viên cho lớp này?")) return;
                teacherMut.mutate(teacherId);
              }}
              statusPending={statusMut.isPending}
              teacherPending={teacherMut.isPending}
            />
          </TabsContent>

          {/* TAB 2: LỊCH HỌC */}
          <TabsContent value="schedule" className="mt-4">
            <ScheduleContent
              classId={cls.id} className={cls.name}
              canMutate={canMutate} currentUserId={currentUserId}
            />
          </TabsContent>

          {/* TAB 3: HỌC SINH */}
          <TabsContent value="students" className="mt-4">
            <StudentsTab
              classId={cls.id} className={cls.name} capacity={cls.capacity}
              canMutate={canMutate} currentUserId={currentUserId}
            />
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Đóng</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============================================================
   EDIT CLASS INFO — full editable form for name/subject/capacity/schedule_note
   ============================================================ */
function EditClassInfo({
  cls, canMutate, teachers, currentUserId, enrollCount, pct,
  onStatusChange, onTeacherChange, statusPending, teacherPending,
}: {
  cls: ClassRow;
  canMutate: boolean;
  teachers: TeacherOpt[];
  currentUserId: string | null;
  enrollCount: number;
  pct: number;
  onStatusChange: (s: ClassStatus) => void;
  onTeacherChange: (teacherId: string) => void;
  statusPending: boolean;
  teacherPending: boolean;
}) {
  const qc = useQueryClient();
  const [name, setName] = React.useState(cls.name);
  const [subject, setSubject] = React.useState(cls.subject ?? "");
  const [capacity, setCapacity] = React.useState(cls.capacity);
  const [scheduleNote, setScheduleNote] = React.useState(cls.schedule_note ?? "");

  React.useEffect(() => {
    setName(cls.name);
    setSubject(cls.subject ?? "");
    setCapacity(cls.capacity);
    setScheduleNote(cls.schedule_note ?? "");
  }, [cls.id, cls.name, cls.subject, cls.capacity, cls.schedule_note]);

  const dirty =
    name.trim() !== cls.name ||
    (subject.trim() || null) !== (cls.subject ?? null) ||
    capacity !== cls.capacity ||
    (scheduleNote.trim() || null) !== (cls.schedule_note ?? null);

  const saveMut = useMutation({
    mutationFn: async () => {
      if (!name.trim()) throw new Error("Tên lớp không được để trống");
      if (capacity < enrollCount) throw new Error(`Sức chứa không được nhỏ hơn sĩ số hiện tại (${enrollCount})`);
      const { error } = await supabase.from("classes").update({
        name: name.trim(),
        subject: subject.trim() || null,
        capacity,
        schedule_note: scheduleNote.trim() || null,
      }).eq("id", cls.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Đã lưu thay đổi");
      qc.invalidateQueries({ queryKey: ["admin", "classes"] });
      notifyClass(cls.id, "Thông tin lớp đã cập nhật",
        `Lớp "${name.trim()}" vừa được cập nhật thông tin.`, "all", currentUserId);
    },
    onError: (e: any) => toast.error(e?.message ?? "Không lưu được"),
  });

  const st = cls.status ?? "active";

  return (
    <div className="space-y-4">
      {cls.class_code && (
        <div className="rounded-lg bg-muted/40 p-3">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">Mã lớp</div>
          <div className="text-sm font-mono font-medium">{cls.class_code}</div>
        </div>
      )}
      <div>
        <Label className="text-xs mb-1.5 block">Tên lớp</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} disabled={!canMutate} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs mb-1.5 block">Môn học</Label>
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} disabled={!canMutate} placeholder="Toán" />
        </div>
        <div>
          <Label className="text-xs mb-1.5 block">Sức chứa</Label>
          <Input type="number" min={1} max={500} value={capacity}
            onChange={(e) => setCapacity(Math.max(1, +e.target.value || 1))} disabled={!canMutate} />
        </div>
      </div>

      <div>
        <div className="flex justify-between text-sm mb-1.5">
          <span className="text-muted-foreground">Sĩ số hiện tại</span>
          <span className="font-medium">{enrollCount} / {capacity}</span>
        </div>
        <div className="h-2.5 bg-muted rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{
            width: `${pct}%`, background: "var(--portal)",
          }} />
        </div>
      </div>

      <div>
        <Label className="text-xs mb-1.5 block">Ghi chú lịch (tóm tắt hiển thị trên thẻ)</Label>
        <Input value={scheduleNote} onChange={(e) => setScheduleNote(e.target.value)}
          disabled={!canMutate} placeholder="VD: T2-T4 · 18:00–19:30" />
      </div>

      <div>
        <Label className="text-xs mb-1.5 block">Trạng thái lớp</Label>
        <Select value={st} onValueChange={(v) => onStatusChange(v as ClassStatus)}>
          <SelectTrigger disabled={!canMutate || statusPending}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Đang học</SelectItem>
            <SelectItem value="paused">Tạm nghỉ</SelectItem>
            <SelectItem value="completed">Đã kết thúc</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-xs mb-1.5 block">Giáo viên phụ trách</Label>
        <TeacherCombobox
          currentTeacherId={cls.teacher_id}
          teachers={teachers}
          disabled={!canMutate || teacherPending}
          onSelect={onTeacherChange}
        />
      </div>

      <div className="flex justify-end gap-2 pt-2 border-t">
        <Button variant="outline" disabled={!dirty || saveMut.isPending}
          onClick={() => {
            setName(cls.name);
            setSubject(cls.subject ?? "");
            setCapacity(cls.capacity);
            setScheduleNote(cls.schedule_note ?? "");
          }}>
          Hoàn tác
        </Button>
        <Button disabled={!canMutate || !dirty || saveMut.isPending}
          onClick={() => saveMut.mutate()}>
          {saveMut.isPending && <Loader2 className="size-4 animate-spin mr-1" />}
          Lưu thay đổi
        </Button>
      </div>
    </div>
  );
}
